import OptionsDefinitions from "./components/OptionsDefinitions";
import DockLayout, { LayoutData, TabData } from "rc-dock";
import "rc-dock/dist/rc-dock.css";
import EquityChart from "./components/EquityChart";
import Main from "./components/Main";
import React, { useEffect, useState } from "react";
import { themeLight } from "dockview-react";
import {
  DockviewApi,
  DockviewReact,
  DockviewReadyEvent,
  IWatermarkPanelProps,
} from "dockview";

import {
  components,
  RightControls,
  tabComponents,
} from "./components/utils/DockerviewSettings";
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { Mobile } from "./components/Mobile";
import {
  MobileNewsSearch,
} from "./components/mobile/NewsSearch";
import { MobileTweetsSearch } from "./components/mobile/TweetsSearch";
import { MobileRedditSearch } from "./components/mobile/RedditSearch";
import { MobileReactions } from "./components/mobile/Reactions";
import { Auth } from "./components/auth/Auth";
import { BottomRevealTray } from "./components/BottomMenu";
import Expressions from "./components/Expressions";
import RequireAuth from "./components/auth/RequireAuth";
import EventCandidates from "./components/EventCandidates";
import { Watchlist } from "./components/Watchlist";
import { ExtendedWatchlist } from "./components/ExtendedWatchlist";
import { Calendar } from "./components/Calendar";
import { MobileObservations } from "./components/mobile/Observations";
import { MobileRealtimeChart } from "./components/mobile/RealtimeChart";
import { MobileRealTimeNotes } from "./components/mobile/RealTimeNotes";
import KK7766comments from "./components/KK7766comments";
import { MobileTwitterRealtime } from "./components/mobile/TwitterRealtime";
import { MobileIBKRChat } from "./components/mobile/IBKRChat";
import { MobileClearCross } from "./components/mobile/ClearCross";
import { resolveEndpoint } from "./components/utils/utils";
import { UpcomingCalendarPanel } from "./components/UpcomingCalendarPanel";
import { useCalendarPanelStore } from "./stores/calendarPanel";
import { MarkedTextNoteWidget } from "./components/MarkedTextNoteWidget";

export function useDarkMode() {
  const getPref = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  const [isDarkMode, setIsDarkMode] = useState(getPref);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);

    // Modern syntax:
    mq.addEventListener("change", handler);
    // Fallback for Safari <14
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isDarkMode;
}

function MainDockviewLayout() {
  const [api, setApi] = useState<DockviewApi>();
  const calendarVisible = useCalendarPanelStore((s) => s.visible);

  const onReady = (event: DockviewReadyEvent) => {
    setApi(event.api);

    event.api.addPanel({
      id: "main_panel",
      component: "main_component",
      tabComponent: "main_tab",
      title: "Main",
    });
  };

  return (
    <div className="flex h-full w-full overflow-hidden">
      <div className="flex-1 min-w-0 h-full relative overflow-hidden">
        <DockviewReact
          theme={themeLight}
          onReady={onReady}
          components={components}
          tabComponents={tabComponents}
          defaultTabComponent={tabComponents["main_tab"]}
          rightHeaderActionsComponent={RightControls}
        />
      </div>
      {calendarVisible && (
        <div className="w-[420px] shrink-0 h-full border-l border-gray-300 dark:border-neutral-700 overflow-hidden">
          <UpcomingCalendarPanel containerApi={api} />
        </div>
      )}
    </div>
  );
}

function App() {
  const isDarkMode = useDarkMode();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // toggle dark class to body
    if (isDarkMode) {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
  }, [isDarkMode]);

  // if not on auth page, check for token
  useEffect(() => {
    if (window.location.pathname !== "/auth") {
      const token = localStorage.getItem("token");
      if (!token) {
        window.location.href = "/auth";
      } else {
        fetch(`${resolveEndpoint()}/test`, {
          method: "GET",
          headers: { authorization: `Bearer ${token}` },
        })
          .then(() => {
            setLoading(false);
          })
          .catch(() => {
            window.location.href = "/auth";
            setLoading(false);
          });
      }
    } else {
      // no loading for token on the auth page
      setLoading(false);
    }
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  const router = createBrowserRouter([
    {
      element: <RequireAuth />,
      children: [
        {
          path: "/",
          element: <MainDockviewLayout />,
        },
        { path: "/mobile", element: <Mobile /> },
        { path: "/mobile/news", element: <MobileNewsSearch /> },
        { path: "/mobile/tweets", element: <MobileTweetsSearch /> },
        { path: "/mobile/reddit-analysis", element: <MobileRedditSearch /> },
        { path: "/mobile/expressions", element: <Expressions /> },
        { path: "/mobile/event-candidates", element: <EventCandidates /> },
        { path: "/mobile/watchlist", element: <Watchlist /> },
        { path: "/mobile/extended-watchlist", element: <ExtendedWatchlist /> },
        { path: "/mobile/calendar", element: <Calendar /> },
        { path: "/mobile/observations", element: <MobileObservations /> },
        { path: "/mobile/real-time-chart", element: <MobileRealtimeChart /> },
        { path: "/mobile/realtime-notes", element: <MobileRealTimeNotes /> },
        { path: "/mobile/kk-comments", element: <KK7766comments /> },
        { path: "/mobile/reactions", element: <MobileReactions /> },
        { path: "/mobile/twitter-realtime", element: <MobileTwitterRealtime /> },
        { path: "/mobile/ibkr-chat", element: <MobileIBKRChat /> },
        { path: "/mobile/clearcross", element: <MobileClearCross /> },
      ],
    },
    { path: "/auth", element: <Auth /> },
  ]);

  return (
    <>
      <RouterProvider router={router} />
      <MarkedTextNoteWidget />
    </>
  );
}

export default App;
